import type { RefundPage } from '@gatecheckpro/contracts';
import {
  REFUND_STATES,
  Card,
  EmptyState,
  LoadingRegion,
  Status,
  formatMoney,
  presentation,
} from '@gatecheckpro/design-system';
import type { Resource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';

/**
 * The refunds already raised, for working through rather than searching.
 *
 * Two entry points exist because operators arrive two ways. Somebody with a
 * customer on the phone starts from the order; somebody working the day's
 * decisions starts here. Neither needs an identifier they would have to be
 * given.
 *
 * The list shows a status per row and nothing else about money's whereabouts,
 * because a refund's meaning depends on the state and abbreviating it into a
 * column would lose exactly the distinction that matters — an approved refund
 * has voided a customer's tickets while their money has not moved, and that is
 * a sentence, not a badge. It is said in full when a row is opened.
 */
export function RefundQueue({
  base,
  page,
  onOpen,
}: {
  base: string;
  page: { resource: Resource<RefundPage>; reload: () => void };
  onOpen: (refundId: string) => void;
}): React.JSX.Element {
  void base;
  return (
    <Card title="Refunds" eyebrow="Raised against this organization">
      {page.resource.status === 'loading' && <LoadingRegion label="Loading refunds" />}
      {page.resource.status === 'failed' && <FailureNotice error={page.resource.error} />}
      {page.resource.status === 'loaded' &&
        (page.resource.data.refunds.length === 0 ? (
          <EmptyState title="No refunds yet">
            Refunds raised against this organization&rsquo;s orders appear here.
          </EmptyState>
        ) : (
          <div className="gc-stack gc-stack--tight">
            {page.resource.data.refunds.map((refund) => {
              const state = presentation(REFUND_STATES, refund.status);
              return (
                <button
                  key={refund.refundId}
                  type="button"
                  className="gc-card gc-card--button"
                  data-testid={`queue-${refund.refundNumber}`}
                  onClick={() => onOpen(refund.refundId)}
                >
                  <span className="gc-row gc-row--between gc-row--baseline">
                    <strong>{refund.refundNumber}</strong>
                    <Status
                      tone={state.tone}
                      glyph={state.glyph}
                      label={state.label}
                      title={state.meaning}
                    />
                  </span>
                  <span className="gc-muted">
                    Order {refund.orderNumber} · {formatMoney(refund.amount)}
                  </span>
                </button>
              );
            })}
            {page.resource.data.nextCursor !== null && (
              // Said rather than silently truncated. A list that stopped without
              // saying so would read as "these are all of them".
              <span className="gc-muted">More refunds exist than are shown here.</span>
            )}
          </div>
        ))}
    </Card>
  );
}
